<template>
  <div class="submit-wrapper">
    <TelegramHeader :tokens="userTokens" />

    <div class="submit-card">
      <h2>Submit Your Story</h2>
      <input v-model="title" type="text" placeholder="Story Title" class="input-field" />
      <textarea v-model="story" placeholder="Write your story here..." class="story-textarea"></textarea>

      <label class="agree-box">
        <input type="checkbox" v-model="agreed" />
        I agree to the
        <span class="terms-link" @click="showTerms = true">Terms and Conditions</span>
      </label>

      <button
        class="submit-button"
        :disabled="!agreed || !title || !story || isSubmitting"
        @click="submitStory"
      >
        {{ isSubmitting ? 'Submitting...' : 'Submit & Earn Token' }}
      </button>
    </div>

    <div v-if="showTerms" class="terms-popup">
      <div class="terms-content">
        <h3>Terms and Conditions</h3>
        <p>By submitting your story, you agree to:</p>
        <ul>
          <li>No plagiarized or offensive content</li>
          <li>Tokens awarded only for original content</li>
          <li>Platform may reject unsuitable stories</li>
        </ul>
        <button @click="showTerms = false">Close</button>
      </div>
    </div>

    <TelegramBottomNav />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import TelegramHeader from '@/components/TelegramHeader.vue';
import TelegramBottomNav from '@/components/TelegramBottomNav.vue';
import { supabase } from '@/supabase';

const title = ref('');
const story = ref('');
const agreed = ref(false);
const showTerms = ref(false);
const isSubmitting = ref(false);
const userTokens = ref(0);

const loadUserTokens = async () => {
  const telegramId = window?.Telegram?.WebApp?.initDataUnsafe?.user?.id;
  if (!telegramId) return;

  const { data, error } = await supabase
    .from('users')
    .select('tokens')
    .eq('telegram_id', telegramId)
    .single();

  if (!error && data) {
    userTokens.value = data.tokens;
  }
};

const submitStory = async () => {
  if (!agreed.value || !title.value || !story.value) {
    alert("Please fill in all fields and agree to the terms.");
    return;
  }

  const telegramId = window?.Telegram?.WebApp?.initDataUnsafe?.user?.id;
  if (!telegramId) {
    alert("Telegram user not detected. Please use the Telegram app.");
    return;
  }

  isSubmitting.value = true;

  const { error: insertError } = await supabase
    .from('stories')
    .insert([
      {
        telegram_id: telegramId,
        title: title.value,
        content: story.value
      }
    ]);

  if (insertError) {
    console.error(insertError);
    alert("❌ Could not save your story. Please try again later.");
    isSubmitting.value = false;
    return;
  }

  const { error: updateError } = await supabase
    .rpc('increment_user_tokens', { telegramid: telegramId });

  if (updateError) {
    console.error(updateError);
    alert("❌ Could not update your tokens. Please try again later.");
    isSubmitting.value = false;
    return;
  }

  await loadUserTokens();

  alert("✅ Story submitted! You earned 1 token.");
  title.value = '';
  story.value = '';
  agreed.value = false;
  isSubmitting.value = false;
};

onMounted(() => {
  loadUserTokens();
});
</script>

<style scoped>
.submit-wrapper {
  background: linear-gradient(180deg, #BB6622, #A14F1A);
  min-height: 100vh;
  color: white;
  display: flex;
  flex-direction: column;
  padding-bottom: 70px;
  font-family: Arial, sans-serif;
}

.submit-card {
  max-width: 500px;
  margin: 30px auto;
  padding: 24px 20px;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  border-radius: 16px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
  text-align: center;
}

.submit-card h2 {
  margin-bottom: 20px;
  font-size: 26px;
  color: #BB6622;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
}

.input-field,
.story-textarea {
  width: 100%;
  margin: 12px 0;
  padding: 14px 16px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 16px;
  box-sizing: border-box;
  transition: box-shadow 0.3s, border-color 0.3s;
}

.input-field:focus,
.story-textarea:focus {
  outline: none;
  border-color: #FFD700;
  box-shadow: 0 0 6px #FFD700;
}

.story-textarea {
  min-height: 160px;
  resize: vertical;
}

.agree-box {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 0;
  font-size: 14px;
  flex-wrap: wrap;
  text-align: center;
}

.terms-link {
  text-decoration: underline;
  cursor: pointer;
  margin-left: 4px;
  color: #BB6622;
}

.submit-button {
  width: 100%;
  padding: 14px;
  background-color: #FFD700;
  color: #333;
  font-weight: bold;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
}

.submit-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.submit-button:hover:not(:disabled) {
  background-color: #ffc300;
  transform: scale(1.02);
}

.terms-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.terms-content {
  background-color: #fff;
  color: #333;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  border-radius: 16px;
  text-align: left;
  box-shadow: 0 8px 16px rgba(0,0,0,0.4);
}

.terms-content h3 {
  margin-top: 0;
  color: #BB6622;
}

.terms-content ul {
  padding-left: 20px;
  margin: 10px 0;
}

.terms-content button {
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #BB6622;
  color: #fff;
  border: none;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.3s;
}

.terms-content button:hover {
  background-color: #A14F1A;
}
</style>